import { useEffect, useMemo, useState } from "react"
import { DictType } from "@pma/types"
import Note from "./note"
import { ScrollArea } from "../common/scroll-area"
import { useSearchParams } from "react-router-dom"
import useTranslator from "@pma/hooks/use-translator"
import DeleteNote from "./delete-note"
import { Trash2 } from "lucide-react"
import { cn } from "@pma/utils/helpers"
import Skeleton from "react-loading-skeleton"

interface Props {
  notes: Array<DictType>
  className?: string
  fromExpandCalling?: boolean
  isSubmitting?: boolean
}
const NotesThread = ({ notes, className = "h-auto", fromExpandCalling = false, isSubmitting = false }: Props) => {
  const [updateThread, setUpdateThread] = useState(false)
  const [searchParam] = useSearchParams()
  const [selectedNotes, setSelectedNotes] = useState([])
  const queryKey = searchParam.get("revalidate")
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  // Get all Main Notes
  const parentMessages = useMemo(
    () => notes.filter((note: DictType) => note?.reply_to_note === 0),
    [notes?.length, updateThread, notes],
  )

  // Get all Replies to Notes
  const repliesToMessages = useMemo(
    () => notes.filter((note: DictType) => note?.reply_to_note !== 0),
    [notes?.length, updateThread, notes],
  )

  useEffect(() => {
    if (queryKey === "tasks") {
      setUpdateThread(true)
    } else {
      setUpdateThread(false)
    }
  }, [queryKey])

  return (
    <>
      {selectedNotes.length > 0 && (
        <div
          className={cn(
            "flex w-[115px] rounded-md border-2 border-solid px-2 py-3 text-xs text-red-500",
            isHebrew ? "rtl" : "ltr",
          )}
        >
          <DeleteNote
            className='flex flex-row items-center justify-center'
            notes={selectedNotes}
            setSelectedNotes={setSelectedNotes}
          >
            <div className='flex w-full items-center gap-2'>
              <span className='min-w-[35px] flex-1 text-left'>{translate("Delete Notes")}</span>
              <Trash2 size={14} />
            </div>
          </DeleteNote>
        </div>
      )}

      {isSubmitting && (
        <div className='my-2 max-w-64 rounded-lg border bg-white p-3 shadow-sm dark:bg-gray-900 md:max-w-[335px]'>
          <div className='flex items-center gap-2'>
            <Skeleton circle width={32} height={32} />
            <Skeleton width={100} height={12} />
          </div>
          <div className='mt-3 ps-3'>
            <Skeleton count={2} height={10} />
          </div>
          <div className='mt-2 flex justify-end'>
            <Skeleton width={60} height={8} />
          </div>
        </div>
      )}

      {parentMessages.length > 0 && (
        <ScrollArea className={`${className} flex gap-3 gap-y-6 pb-6`}>
          {parentMessages?.map((message: DictType, index) => (
            <Note
              key={message?.id}
              message={message}
              messages={repliesToMessages}
              setSelectedNotes={setSelectedNotes}
              selectedNotes={selectedNotes}
              fromExpandCalling={fromExpandCalling}
            />
          ))}
        </ScrollArea>
      )}
    </>
  )
}

export default NotesThread
